import React, { useState } from "react";
import { ShareNetwork, Copy, X, WhatsappLogo, FacebookLogo, Envelope, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

/**
 * Share button with native Web Share API (mobile) + popover fallback (desktop).
 * Props:
 *  - url: full URL to share (defaults to current page)
 *  - title: shareable title
 *  - text: shareable description
 *  - compact: bool — small inline variant for cards
 */
export default function ShareButton({ url, title, text, compact = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const safeTitle = title || "Mon Commerce 68";
  const safeText = text || "";

  const tryNativeShare = async (e) => {
    e?.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: safeTitle, text: safeText, url: fullUrl });
        return true;
      } catch { /* user cancelled — ignore */ }
    }
    setOpen(true);
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success(t.share.link_copied);
      setOpen(false);
    } catch {
      // Fallback: textarea trick
      const ta = document.createElement("textarea");
      ta.value = fullUrl; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy"); ta.remove();
      toast.success(t.share.link_copied_short);
      setOpen(false);
    }
  };

  const enc = encodeURIComponent;
  const shareWhatsapp = `https://wa.me/?text=${enc(safeTitle + " — " + fullUrl)}`;
  const shareFacebook = `https://www.facebook.com/sharer/sharer.php?u=${enc(fullUrl)}`;
  const shareMessenger = `https://www.facebook.com/dialog/send?link=${enc(fullUrl)}&app_id=140586622674265&redirect_uri=${enc(fullUrl)}`;
  const shareEmail = `mailto:?subject=${enc(safeTitle)}&body=${enc(safeText + "\n\n" + fullUrl)}`;
  const shareSms = `sms:?&body=${enc(safeTitle + " — " + fullUrl)}`;

  const btnBase = compact
    ? "p-1.5 hover:bg-accent rounded-sm text-muted-foreground hover:text-primary"
    : "inline-flex items-center gap-2 px-3 py-2 bg-card border border-border hover:bg-accent rounded-sm text-sm font-medium";

  return (
    <div className="relative inline-block" data-testid="share-btn-wrap">
      <button onClick={tryNativeShare} className={btnBase} aria-label={t.share.button} data-testid="share-btn">
        <ShareNetwork size={compact ? 14 : 16} weight={compact ? "regular" : "bold"}/>
        {!compact && <span>{t.share.button}</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute z-50 right-0 mt-2 bg-card border border-border rounded-sm shadow-lg p-3 min-w-[240px]" data-testid="share-popover">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t.share.via}</div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label={t.share.close}><X size={14}/></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <ShareItem icon={<WhatsappLogo size={20} weight="duotone"/>} label="WhatsApp" href={shareWhatsapp} testid="share-whatsapp"/>
              <ShareItem icon={<FacebookLogo size={20} weight="duotone"/>} label="Facebook" href={shareFacebook} testid="share-facebook"/>
              <ShareItem icon={<ChatCircleText size={20} weight="duotone"/>} label="Messenger" href={shareMessenger} testid="share-messenger"/>
              <ShareItem icon={<Envelope size={20} weight="duotone"/>} label="Email" href={shareEmail} testid="share-email"/>
              <ShareItem icon={<ChatCircleText size={20} weight="duotone"/>} label="SMS" href={shareSms} testid="share-sms"/>
              <button onClick={copyLink} className="flex flex-col items-center gap-1 p-2 hover:bg-accent rounded-sm text-xs" data-testid="share-copy">
                <Copy size={20} weight="duotone"/>
                <span>{t.share.copy}</span>
              </button>
            </div>
            <div className="mt-3 text-[10px] text-muted-foreground break-all bg-accent/20 p-2 rounded-sm">{fullUrl}</div>
          </div>
        </>
      )}
    </div>
  );
}

function ShareItem({ icon, label, href, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className="flex flex-col items-center gap-1 p-2 hover:bg-accent rounded-sm text-xs text-foreground" data-testid={testid}>
      {icon}
      <span>{label}</span>
    </a>
  );
}
